import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { api, apiError } from "@/services/api"
import type { Workplace, WorkplaceCreate } from "@/types"

interface WorkplaceSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = Neuanlage */
  workplace: Workplace | null
  canWrite: boolean
}

/** Die 6 Zulagenfelder eines Arbeitsplatzes (G-15): 3 Arten × (€/h + %). */
const SURCHARGE_FIELDS = [
  { key: "noiseSurchargeRate", label: "Lärmzulage (€/h)" },
  { key: "noiseSurchargePct", label: "Lärmzulage (%)" },
  { key: "acidDustSurchargeRate", label: "Säure/Staub-Zulage (€/h)" },
  { key: "acidDustSurchargePct", label: "Säure/Staub-Zulage (%)" },
  { key: "displacementSurchargeRate", label: "Verdrängungszulage (€/h)" },
  { key: "displacementSurchargePct", label: "Verdrängungszulage (%)" },
] as const

type SurchargeKey = (typeof SURCHARGE_FIELDS)[number]["key"]

export function WorkplaceSheet({ open, onOpenChange, workplace, canWrite }: WorkplaceSheetProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [riskAssessment, setRiskAssessment] = useState("")
  const [surcharges, setSurcharges] = useState<Record<SurchargeKey, string>>({
    noiseSurchargeRate: "",
    noiseSurchargePct: "",
    acidDustSurchargeRate: "",
    acidDustSurchargePct: "",
    displacementSurchargeRate: "",
    displacementSurchargePct: "",
  })

  useEffect(() => {
    if (!open) return
    setName(workplace?.name ?? "")
    setDescription(workplace?.description ?? "")
    setRiskAssessment(workplace?.riskAssessment ?? "")
    setSurcharges({
      noiseSurchargeRate: workplace?.noiseSurchargeRate?.toString() ?? "",
      noiseSurchargePct: workplace?.noiseSurchargePct?.toString() ?? "",
      acidDustSurchargeRate: workplace?.acidDustSurchargeRate?.toString() ?? "",
      acidDustSurchargePct: workplace?.acidDustSurchargePct?.toString() ?? "",
      displacementSurchargeRate: workplace?.displacementSurchargeRate?.toString() ?? "",
      displacementSurchargePct: workplace?.displacementSurchargePct?.toString() ?? "",
    })
  }, [open, workplace])

  const mutation = useMutation({
    mutationFn: (body: WorkplaceCreate) =>
      workplace
        ? api.organization.workplaces.update(workplace.id, body)
        : api.organization.workplaces.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workplaces"] })
      toast.success(workplace ? "Arbeitsplatz aktualisiert" : "Arbeitsplatz angelegt")
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function submit() {
    const body: WorkplaceCreate = {
      name: name.trim(),
      description: description.trim() || null,
      riskAssessment: riskAssessment.trim() || null,
    }
    for (const { key } of SURCHARGE_FIELDS) {
      body[key] = surcharges[key] !== "" ? surcharges[key] : null
    }
    mutation.mutate(body)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="workplace" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{workplace ? workplace.name : "Neuer Arbeitsplatz"}</SheetTitle>
          <SheetDescription>
            {workplace
              ? "Details, Gefährdungsbeurteilung und Zulagensätze."
              : "Neuen Arbeitsplatz anlegen."}
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="wp-name">Name</Label>
            <Input
              id="wp-name"
              value={name}
              disabled={!canWrite}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wp-description">Beschreibung</Label>
            <Textarea
              id="wp-description"
              value={description}
              disabled={!canWrite}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wp-risk">Gefährdungsbeurteilung</Label>
            <Input
              id="wp-risk"
              placeholder="z. B. GBU-NIT-2025"
              value={riskAssessment}
              disabled={!canWrite}
              onChange={(e) => setRiskAssessment(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {SURCHARGE_FIELDS.map(({ key, label }) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`wp-${key}`}>{label}</Label>
                <Input
                  id={`wp-${key}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={surcharges[key]}
                  disabled={!canWrite}
                  onChange={(e) => setSurcharges((s) => ({ ...s, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </div>
        {canWrite && (
          <SheetFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button disabled={name.trim() === "" || mutation.isPending} onClick={submit}>
              Speichern
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}
